import React, {
  useCallback,
  useEffect,
  useRef,
} from 'react';
import PropTypes from 'prop-types';
import { SVG } from '@svgdotjs/svg.js';
import svgPanZoom from 'svg-pan-zoom';
import Hammer from 'hammerjs';
import { animate } from 'framer-motion';

import { Home } from '../../ui/icon/Home';
import { PlusLight } from '../../ui/icon/PlusLight';
import { MinusLight } from '../../ui/icon/MinusLight';
import { identityModel } from '../../../redux-modules/base/models';

import './fair-interactive-map.css';

function FairInteractiveMap({
  children,
  stands,
  pois,
  standsInFocus,
  standsFavourites,
  standsVisited,
  panGutter,
  bgColor,
  disableBgColor,
  notInFocusOpacity,
  fingersToPan,
  withoutControls,
  withoutHome,
  withoutPan,
  withoutZoom,
  onlyFocusClickable,
  onStandClick,
  onOutsideClick,
}) {
  const elementRef = useRef(null);
  const zoomInElementRef = useRef(null);
  const zoomOutElementRef = useRef(null);
  const svg = useRef(null);
  const svgApi = useRef(null);

  const standElements = useRef([]);
  const entranceElement = useRef(null);
  const entranceButtonElement = useRef(null);
  const currentStandsInFocus = useRef([]);
  const cachedZoomSettings = useRef(null);
  const isPinching = useRef(false);
  const isPanning = useRef(false);
  const isAnimationInit = useRef(false);

  const zoomAnimationTimer = useRef(null);
  const lastStandClickTime = useRef(null);

  const getZoomSettings = useCallback(() => {
    const isLoaded = svgApi.current;
    if (!isLoaded) {
      return {
        maxZoom: 2,
        minZoom: 1,
        maxAutoZoom: 1.6,
        minAutoZoom: 1,
        zoomScaleSensitivity: 0.2,
      };
    }
    const rect = elementRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
    );

    if (cachedZoomSettings.current?.visibleWidth === visibleWidth) {
      return cachedZoomSettings.current;
    }

    const steps = 8;
    const stepIncrement = 0.1;
    let widthFactor = 1;
    if (visibleWidth < 1280) {
      widthFactor = 2;
    }
    if (visibleWidth < 768) {
      widthFactor = 3;
    }

    const rawMinZoom = (visibleWidth * widthFactor) / svgApi.current.getSizes().width;
    const rawMaxZoom = 2 * rawMinZoom;

    const minZoom = Math.ceil(rawMinZoom / stepIncrement) * stepIncrement;
    const maxZoom = Math.floor(rawMaxZoom / stepIncrement) * stepIncrement;
    const zoomScaleSensitivity = (maxZoom - minZoom) / steps;
    const maxAutoZoom = Math.min(
      Math.ceil((1.6 * minZoom) / stepIncrement) * stepIncrement,
      maxZoom,
    );

    cachedZoomSettings.current = {
      visibleWidth,
      maxZoom,
      minZoom,
      maxAutoZoom,
      minAutoZoom: minZoom,
      zoomScaleSensitivity,
    };

    return cachedZoomSettings.current;
  }, []);

  const limitPan = useCallback((newPan) => {
    const rect = elementRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
    );
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
    );
    const { realZoom, viewBox } = svgApi.current?.getSizes() || {
      realZoom: 0,
      viewBox: {
        width: 0,
        height: 0,
      },
    };

    const leftLimit = (panGutter?.left || 30);
    const rightLimit = -(viewBox.width * realZoom) + visibleWidth - (panGutter?.right || 30);
    const topLimit = (panGutter?.top || 30);
    const bottomLimit = -(viewBox.height * realZoom) + visibleHeight - (panGutter?.bottom || 30);

    return {
      x: Math.min(leftLimit, Math.max(rightLimit, newPan.x)),
      y: Math.min(topLimit, Math.max(bottomLimit, newPan.y)),
    };
  }, [panGutter]);

  const handleBeforePan = useCallback((oldPan, newPan) => limitPan(newPan), [limitPan]);

  const zoomToEntrance = useCallback(() => {
    if (!entranceElement.current) {
      return;
    }

    const { cx, cy } = entranceElement.current.rbox();

    const rect = elementRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
    );
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
    );

    const xOffset = (visibleWidth / 2)
      + rect.left
      - ((panGutter?.right || 30) - (panGutter?.left || 30));
    const yOffset = (visibleHeight / 2)
      + rect.top
      - ((panGutter?.bottom || 30) - (panGutter?.top || 30));

    const currentPan = svgApi.current.getPan();
    const zoomSettings = getZoomSettings();

    const pan = {
      x: -(cx - xOffset) + currentPan.x,
      y: -(cy - yOffset) + currentPan.y,
    };
    const zoom = {
      x: xOffset - rect.left,
      y: yOffset - rect.top,
    };
    const zoomFactor = zoomSettings.minAutoZoom + zoomSettings.zoomScaleSensitivity;

    if (zoomInElementRef.current) {
      zoomInElementRef.current.disabled = false;
    }
    if (zoomOutElementRef.current) {
      zoomOutElementRef.current.disabled = false;
    }

    const zoomChanged = Math.abs(zoomFactor - svgApi.current.getZoom()) > 0.01;
    if (zoomChanged) {
      svgApi.current.setBeforePan(null);
      svgApi.current.pan(pan);
      svgApi.current.setBeforePan(handleBeforePan);
      svgApi.current.zoomAtPoint(zoomFactor, zoom);
    } else {
      svgApi.current.pan(limitPan(pan));
    }
  }, [
    panGutter,
    getZoomSettings,
    limitPan,
    handleBeforePan,
  ]);

  const zoomToStands = useCallback((standNumbers) => {
    const isLoaded = svgApi.current && svg.current;
    if (!isLoaded) {
      return;
    }
    const standsInFocusAsText = !standNumbers || standNumbers.length === 0
      ? []
      : Array
        .from(new Set(standNumbers.map((s) => s.toString().padStart(2, '0'))))
        .sort((a, b) => a.localeCompare(b));
    if (standsInFocusAsText.length === currentStandsInFocus.current.length
      && JSON.stringify(standsInFocusAsText) === JSON.stringify(currentStandsInFocus.current)
    ) {
      return;
    }
    const previousStandsInFocus = currentStandsInFocus.current;
    currentStandsInFocus.current = standsInFocusAsText;

    const activeStands = [];
    const {
      x,
      x2,
      cx,
      y,
      y2,
      cy,
    } = standElements.current
      .filter((standElement) => {
        const standNumber = standElement.remember('standNumber');
        const standActive = standElement.remember('standActive');
        const isInFocus = standNumber && standsInFocusAsText.includes(standNumber);
        if (standActive) {
          activeStands[standNumber] = true;
          standElement.css({ opacity: isInFocus ? 1 : (notInFocusOpacity || 0.5) });
        }
        return isInFocus;
      })
      .reduce((accumulator, standElement, index, array) => {
        const standElementBox = standElement.rbox();
        if (accumulator.x > standElementBox.x) {
          accumulator.x = standElementBox.x;
        }
        if (accumulator.x2 < standElementBox.x2) {
          accumulator.x2 = standElementBox.x2;
        }
        if (accumulator.y > standElementBox.y) {
          accumulator.y = standElementBox.y;
        }
        if (accumulator.y2 < standElementBox.y2) {
          accumulator.y2 = standElementBox.y2;
        }
        accumulator.cx += standElementBox.cx;
        accumulator.cy += standElementBox.cy;
        if (index === array.length - 1) {
          accumulator.cx /= array.length;
          accumulator.cy /= array.length;
        }
        return accumulator;
      }, {
        x: 9999,
        x2: 0,
        cx: 0,
        y: 9999,
        y2: 0,
        cy: 0,
      });

    const numberOfActiveStands = Object.keys(activeStands).length;
    const shouldZoomToEntrance = entranceElement.current
      && numberOfActiveStands <= standsInFocusAsText.length
      && numberOfActiveStands > 1;

    const skipZoom = (x === 9999 && x2 === 0 && y === 9999 && y2 === 0)
      || ((previousStandsInFocus || []).length === 1 && shouldZoomToEntrance);
    if (skipZoom) {
      return;
    }

    if (shouldZoomToEntrance) {
      zoomToEntrance();
      return;
    }

    const rect = elementRef.current?.getBoundingClientRect() || {
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
    const visibleWidth = Math.max(
      0,
      Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
    );
    const visibleHeight = Math.max(
      0,
      Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
    );

    const xOffset = (visibleWidth / 2)
      + rect.left
      - ((panGutter?.right || 30) - (panGutter?.left || 30));
    const yOffset = (visibleHeight / 2)
      + rect.top
      - ((panGutter?.bottom || 30) - (panGutter?.top || 30));

    const currentPan = svgApi.current.getPan();
    const zoomSettings = getZoomSettings();

    const pan = {
      x: -(cx - xOffset) + currentPan.x,
      y: -(cy - yOffset) + currentPan.y,
    };
    const zoom = {
      x: xOffset - rect.left,
      y: yOffset - rect.top,
    };

    let zoomFactor = Math.min(
      zoomSettings.maxAutoZoom,
      ((visibleWidth - rect.left) / ((x2 - x) + 100)),
      ((visibleHeight - rect.top * 2) / ((y2 - y) + 100)),
    );
    zoomFactor = Math.max(zoomSettings.minAutoZoom, zoomFactor);

    const canZoomIn = zoomFactor < (zoomSettings.maxZoom - 0.01);
    const canZoomOut = zoomFactor > (zoomSettings.minZoom + 0.01);
    if (zoomInElementRef.current) {
      zoomInElementRef.current.disabled = !canZoomIn;
    }
    if (zoomOutElementRef.current) {
      zoomOutElementRef.current.disabled = !canZoomOut;
    }

    const zoomChanged = Math.abs(zoomFactor - svgApi.current.getZoom()) > 0.01;
    if (zoomChanged) {
      svgApi.current.setBeforePan(null);
      svgApi.current.pan(pan);
      svgApi.current.setBeforePan(handleBeforePan);
      svgApi.current.zoomAtPoint(zoomFactor, zoom);
    } else {
      svgApi.current.pan(limitPan(pan));
    }
  }, [
    panGutter,
    notInFocusOpacity,
    getZoomSettings,
    limitPan,
    zoomToEntrance,
    handleBeforePan,
  ]);

  const setZoomLevel = useCallback((zoomLevel, point = null) => {
    const isLoaded = svgApi.current && svg.current;
    if (!isLoaded) {
      return;
    }
    const isZoomLevelChanged = zoomLevel && Math.abs(zoomLevel - svgApi.current.getZoom()) > 0.01;
    if (!isZoomLevelChanged) {
      return;
    }

    let pointToZoom = point;
    if (!pointToZoom) {
      const rect = elementRef.current?.getBoundingClientRect() || {
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      };
      const visibleWidth = Math.max(
        0,
        Math.min(rect.right, window.innerWidth) - Math.max(rect.left, 0),
      );
      const visibleHeight = Math.max(
        0,
        Math.min(rect.bottom, window.innerHeight) - Math.max(rect.top, 0),
      );
      pointToZoom = {
        x: visibleWidth / 2,
        y: visibleHeight / 2,
      };
    }
    const zoomSettings = getZoomSettings();

    const clampZoomLevel = Math.max(
      zoomSettings.minZoom,
      Math.min(zoomLevel, zoomSettings.maxZoom),
    );
    svgApi.current.zoomAtPoint(clampZoomLevel, pointToZoom);
  }, [getZoomSettings]);

  const adjustZoomLevelBy = useCallback((zoomLevel, point = null) => {
    const isLoaded = svgApi.current && svg.current;
    if (!isLoaded) {
      return;
    }
    setZoomLevel(svgApi.current.getZoom() + zoomLevel, point);
  }, [setZoomLevel]);

  const handleHome = useCallback(() => {
    zoomToEntrance();
  }, [zoomToEntrance]);

  const handleZoomIn = useCallback(() => {
    const zoomSettings = getZoomSettings();
    adjustZoomLevelBy(zoomSettings.zoomScaleSensitivity);
  }, [getZoomSettings, adjustZoomLevelBy]);

  const handleZoomOut = useCallback(() => {
    const zoomSettings = getZoomSettings();
    adjustZoomLevelBy(-zoomSettings.zoomScaleSensitivity);
  }, [getZoomSettings, adjustZoomLevelBy]);

  const handleBeforeZoom = useCallback(() => {
    const viewport = svg.current?.node?.querySelector('.svg-pan-zoom_viewport');
    if (viewport) {
      const preventAnimation = isPinching.current
        || !isAnimationInit.current
        || withoutZoom;
      viewport.style.transition = !preventAnimation ? 'transform 0.3s ease-in' : null;
      isAnimationInit.current = true;
    }
  }, [withoutZoom]);

  const handleZoom = useCallback((zoom) => {
    const viewport = svg.current?.node?.querySelector('.svg-pan-zoom_viewport');
    if (viewport) {
      if (zoomAnimationTimer.current) {
        clearTimeout(zoomAnimationTimer.current);
      }
      zoomAnimationTimer.current = setTimeout(() => {
        viewport.style.transition = null;
        zoomAnimationTimer.current = null;
      }, 300);
    }
    const zoomSettings = getZoomSettings();
    const canZoomIn = zoom < (zoomSettings.maxZoom - 0.01);
    const canZoomOut = zoom > (zoomSettings.minZoom + 0.01);
    if (zoomInElementRef.current) {
      zoomInElementRef.current.disabled = !canZoomIn;
    }
    if (zoomOutElementRef.current) {
      zoomOutElementRef.current.disabled = !canZoomOut;
    }
  }, [getZoomSettings]);

  const handleStandClick = useCallback((standNumber) => {
    if (standNumber && onStandClick && !isPanning.current && !isPinching.current) {
      lastStandClickTime.current = Date.now();
      onStandClick(standNumber.toString().replace(/^0+/, ''));
    }
  }, [onStandClick]);

  const handleOutsideClick = useCallback(() => {
    const timeSinceLastStandClick = Date.now() - (lastStandClickTime.current || 0);
    if (onOutsideClick
      && !isPanning.current
      && !isPinching.current
      && timeSinceLastStandClick > 300
    ) {
      onOutsideClick();
    }
  }, [onOutsideClick]);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.body.style.overflow = 'hidden';

    const element = elementRef.current;

    return () => {
      if (svgApi.current) {
        svgApi.current.destroy();
        svgApi.current = null;
      }
      if (svg.current) {
        svg.current = null;
      }
      if (element) {
        element.innerHTML = '';
      }
      (standElements.current || []).forEach((standElement) => {
        const originalColor = standElement.remember('originalColor');
        if (originalColor) {
          standElement.css({ fill: originalColor });
        }
        standElement.off();
      });
      document.body.style.overflow = '';
    };
  }, []);

  useEffect(() => {
    const handleResize = () => {
      if (svgApi.current) {
        const zoomSettings = getZoomSettings();
        svgApi.current.setMaxZoom(zoomSettings.maxZoom);
        svgApi.current.setMinZoom(zoomSettings.minZoom);
        svgApi.current.setZoomScaleSensitivity(zoomSettings.zoomScaleSensitivity);
      }
    };
    const handleWheelZoom = (event) => {
      event.preventDefault();
      if (!svgApi.current) {
        return;
      }

      const zoomSettings = getZoomSettings();
      if (event.deltaY < 0) {
        adjustZoomLevelBy(zoomSettings.zoomScaleSensitivity, { x: event.x, y: event.y });
      } else {
        adjustZoomLevelBy(-zoomSettings.zoomScaleSensitivity, { x: event.x, y: event.y });
      }
    };

    window.addEventListener('resize', handleResize);
    const element = elementRef.current;
    if (element && !withoutZoom) {
      element.addEventListener('wheel', handleWheelZoom, { passive: false });
    }

    return () => {
      window.removeEventListener('resize', handleResize);
      if (element && !withoutZoom) {
        element.removeEventListener('wheel', handleWheelZoom);
      }
    };
  }, [withoutZoom, getZoomSettings, adjustZoomLevelBy]);

  useEffect(() => {
    const isLoaded = svgApi.current && svg.current;
    if (isLoaded) {
      return () => {};
    }

    if (elementRef.current) {
      elementRef.current.style.visibility = 'hidden';
      if (elementRef.current.nextSibling) {
        elementRef.current.nextSibling.style.visibility = 'hidden';
      }
    }

    const initTimeoutId = setTimeout(() => {
      if (!React.isValidElement(children)) {
        elementRef.current.innerHTML = '';
        elementRef.current.appendChild(children);
      }
      const svgElement = elementRef.current?.querySelector('svg') || null;
      if (svgElement && stands && stands.length > 0) {
        const customEventsHandler = {
          haltEventListeners: [
            'mouseup',
            'mousedown',
            'mousemove',
            'touchstart',
            'touchend',
            'touchmove',
            'touchleave',
            'touchcancel',
          ],
          hammerInstance: null,
          init: (options) => {
            const { instance, svgElement: svgPanZoomElement } = options;

            isPanning.current = false;
            let initialScale = 1;
            let panningValues = {
              pannedX: 0,
              pannedY: 0,
              velocityX: 0,
              velocityY: 0,
            };

            const isTouchDevice = ('ontouchstart' in window) || (navigator.maxTouchPoints > 0);

            const hammer = Hammer(svgPanZoomElement);
            customEventsHandler.hammerInstance = hammer;

            hammer.get('pinch').set({ enable: !withoutZoom });
            hammer.get('pan').set({
              enable: !withoutPan,
              pointers: (isTouchDevice && fingersToPan && fingersToPan > 1) ? fingersToPan : 1,
              direction: Hammer.DIRECTION_ALL,
            });

            hammer.on('tap', () => setTimeout(() => handleOutsideClick(), 0));
            hammer.on('panstart', () => {
              if (elementRef.current) {
                elementRef.current.style.cursor = 'grabbing';
              }
              isPanning.current = true;
              panningValues = {
                ...panningValues,
                pannedX: 0,
                pannedY: 0,
                velocityX: 0,
                velocityY: 0,
              };
            });
            hammer.on('panmove', (ev) => {
              if (!isPanning.current) {
                return;
              }
              const diffX = ev.deltaX - panningValues.pannedX;
              const diffY = ev.deltaY - panningValues.pannedY;
              panningValues.velocityX = ev.velocityX;
              panningValues.velocityY = ev.velocityY;
              instance.panBy({ x: diffX * 0.95, y: diffY * 0.95 });
              panningValues.pannedX = ev.deltaX;
              panningValues.pannedY = ev.deltaY;
            });
            hammer.on('panend', () => {
              if (elementRef.current) {
                elementRef.current.style.cursor = 'grab';
              }
              if (!isPanning.current) {
                return;
              }
              let animationsComplete = 0;
              animate(panningValues.velocityX, 0, {
                duration: 0.5,
                ease: 'easeOut',
                onUpdate: (currentVelocity) => {
                  instance.panBy({ x: currentVelocity * 12, y: 0 });
                },
                onComplete: () => {
                  animationsComplete += 1;
                  if (animationsComplete === 2) {
                    isPanning.current = false;
                  }
                },
              });
              animate(panningValues.velocityY, 0, {
                duration: 0.5,
                ease: 'easeOut',
                onUpdate: (currentVelocity) => {
                  instance.panBy({ x: 0, y: currentVelocity * 12 });
                },
                onComplete: () => {
                  animationsComplete += 1;
                  if (animationsComplete === 2) {
                    isPanning.current = false;
                  }
                },
              });
            });
            hammer.on('pinchstart', (ev) => {
              isPinching.current = true;
              initialScale = instance.getZoom();
              instance.zoomAtPoint(initialScale * ev.scale, { x: ev.center.x, y: ev.center.y });
            });
            hammer.on('pinchmove', (ev) => {
              instance.zoomAtPoint(initialScale * ev.scale, { x: ev.center.x, y: ev.center.y });
            });
            hammer.on('pinchend', () => {
              isPinching.current = false;
            });
            svgPanZoomElement.addEventListener('touchmove', (ev) => {
              ev.preventDefault();
            });
          },
          destroy: () => {
            if (customEventsHandler.hammerInstance) {
              customEventsHandler.hammerInstance.destroy();
              customEventsHandler.hammerInstance = null;
            }
          },
        };

        const viewBox = svgElement.getAttribute('viewBox');
        const oldViewBox = svgElement.dataset.viewBox;
        if (viewBox) {
          svgElement.dataset.viewBox = viewBox;
        }
        if (oldViewBox) {
          svgElement.setAttribute('viewBox', oldViewBox);
        }

        svgApi.current = svgPanZoom(svgElement, {
          dblClickZoomEnabled: false,
          fit: false,
          center: false,
          mouseWheelZoomEnabled: false,
          customEventsHandler,
          beforePan: handleBeforePan,
          beforeZoom: handleBeforeZoom,
          onZoom: handleZoom,
        });
        const zoomSettings = getZoomSettings();
        svgApi.current.setMaxZoom(zoomSettings.maxZoom);
        svgApi.current.setMinZoom(zoomSettings.minZoom);
        svgApi.current.setZoomScaleSensitivity(zoomSettings.zoomScaleSensitivity);

        const standsAsText = stands.map((s) => s.toString().padStart(2, '0'));
        const poisAsText = pois.map((p) => p.toLowerCase().trim());
        const standsInFocusAsText = standsInFocus.map((s) => s.toString().padStart(2, '0'));
        const standsFavoritesAsText = Array.from(new Set(standsFavourites)).map((s) => s.toString().padStart(2, '0'));
        const standsVisitedAsText = Array.from(new Set(standsVisited)).map((s) => s.toString().padStart(2, '0'));
        const disabledStands = Array
          .from({ length: 999 }, (_, i) => `${(i + 1).toString().padStart(2, '0')}`)
          .filter((standNumber) => !standsAsText.includes(standNumber));
        svg.current = SVG(svgElement);

        requestAnimationFrame(() => {
          const textElements = svg.current.find('text');
          const bgColorSanitized = bgColor ? bgColor.toLowerCase() : null;
          const boxElements = svg.current.find('rect, path, polygon, polyline').filter((element) => {
            const { fill } = element.attr(['fill']);
            const fillSanitized = fill ? fill.toLowerCase() : null;
            const hasFill = fillSanitized
              && fillSanitized !== 'none'
              && fillSanitized !== 'transparent'
              && fillSanitized !== '#00000000'
              && fillSanitized !== '#000000'
              && fillSanitized !== '#ffffffff'
              && fillSanitized !== '#ffffff'
              && fillSanitized !== bgColorSanitized;

            return hasFill;
          });
          standElements.current = textElements
            .filter((textElement) => {
              const rawText = textElement.text().trim();
              if (!rawText) {
                return false;
              }

              const text = rawText.replace(/[\s\n]+/g, ' ').trim().toLowerCase();
              const isNumber = /^\d/.test(text);
              const isPoi = !isNumber && poisAsText.length > 0 && poisAsText.includes(text);
              const isEntrance = !isNumber && !isPoi && text.includes('entrance');

              if (isEntrance) {
                if (text === 'entrance') {
                  entranceElement.current = textElement;
                } else if (!entranceElement.current) {
                  entranceElement.current = textElement;
                }
                if (entranceButtonElement.current && !withoutHome) {
                  entranceButtonElement.current.style.display = null;
                }
                return false;
              }

              const isValidStand = isNumber || isPoi;
              if (!isValidStand) {
                return false;
              }

              let stand = null;
              if (isNumber) {
                stand = standsAsText.find((standNumber) => {
                  const regex = new RegExp(`^${standNumber}(\\W|$)`);
                  return regex.test(text);
                });
              }
              if (isPoi) {
                stand = text;
              }

              if (stand) {
                textElement.remember('standNumber', stand);
                textElement.remember('standActive', true);
                textElement.remember('standType', (isPoi ? 'poi' : 'stand'));
                return true;
              }
              const disabledStand = disabledStands.find((standNumber) => {
                const regex = new RegExp(`^${standNumber}(\\W|$)`);
                return regex.test(text);
              });
              if (disabledStand) {
                textElement.remember('standNumber', disabledStand);
                textElement.remember('standActive', false);
                textElement.remember('standType', 'stand');
                return true;
              }
              return false;
            })
            .map((textElement) => {
              const textBox = textElement.rbox();
              const boxes = boxElements.filter((element) => {
                if (element === textElement) {
                  return false;
                }

                const elementBox = element.rbox();
                if (elementBox.width <= textBox.width) {
                  return false;
                }
                if (elementBox.height <= textBox.height) {
                  return false;
                }
                if (elementBox.width >= (textBox.width * 10)) {
                  return false;
                }

                const tolerance = 4;

                return textBox.x >= elementBox.x - tolerance
                  && textBox.x + textBox.width <= elementBox.x + elementBox.width + tolerance
                  && textBox.y >= elementBox.y - tolerance
                  && textBox.y + textBox.height <= elementBox.y + elementBox.height + tolerance;
              }).sort((a, b) => {
                const relationA = a.node.compareDocumentPosition(textElement.node);
                const relationB = b.node.compareDocumentPosition(textElement.node);
                // eslint-disable-next-line no-bitwise
                const isABelow = relationA & Node.DOCUMENT_POSITION_PRECEDING;
                // eslint-disable-next-line no-bitwise
                const isBBelow = relationB & Node.DOCUMENT_POSITION_PRECEDING;
                if (isABelow && !isBBelow) {
                  return -1;
                }
                if (!isABelow && isBBelow) {
                  return 1;
                }
                const relationBetweenAB = a.node.compareDocumentPosition(b.node);
                // eslint-disable-next-line no-bitwise
                if (relationBetweenAB & Node.DOCUMENT_POSITION_PRECEDING) {
                  return -1;
                }
                // eslint-disable-next-line no-bitwise
                if (relationBetweenAB & Node.DOCUMENT_POSITION_FOLLOWING) {
                  return 1;
                }

                return 0;
              });

              const standNumber = textElement.remember('standNumber');
              const standActive = textElement.remember('standActive');
              const standType = textElement.remember('standType');

              const isFavourite = standsFavoritesAsText.includes(standNumber);
              const isVisited = standsVisitedAsText.includes(standNumber);
              const hasMarker = isFavourite || isVisited;
              if (hasMarker) {
                const parentGroup = textElement.parent();
                const transform = textElement.transform();
                const bbox = textElement.bbox();
                if (isFavourite) {
                  parentGroup
                    .circle(bbox.height)
                    .fill('#fe0404')
                    .transform(transform)
                    .move(bbox.x, (bbox.y - bbox.height));
                  parentGroup
                    .path('M1.83594 11.2578C0.664062 10.1641 0 8.60156 0 6.96094V6.76562C0 4.03125 1.95312 1.6875 4.64844 1.25781C6.44531 0.945312 8.24219 1.53125 9.53125 2.78125L10 3.25L10.4688 2.78125C11.7188 1.53125 13.5547 0.945312 15.3125 1.25781C18.0078 1.6875 20 4.03125 20 6.76562V6.96094C20 8.60156 19.2969 10.1641 18.125 11.2578L11.0547 17.8594C10.7812 18.1328 10.3906 18.25 10 18.25C9.57031 18.25 9.17969 18.1328 8.90625 17.8594L1.83594 11.2578Z')
                    .fill('white')
                    .size(bbox.height * 0.6, bbox.height * 0.6)
                    .transform(transform)
                    .move((bbox.x + bbox.height * 0.2), (bbox.y - bbox.height * 0.8));
                }
                if (isVisited) {
                  parentGroup
                    .circle(bbox.height)
                    .fill('#13c53a')
                    .transform(transform)
                    .move((bbox.x + bbox.height), (bbox.y - bbox.height));
                  parentGroup
                    .path('M288 32c-80.8 0-145.5 36.8-192.6 80.6C48.6 156 17.3 208 2.5 243.7c-3.3 7.9-3.3 16.7 0 24.6C17.3 304 48.6 356 95.4 399.4C142.5 443.2 207.2 480 288 480s145.5-36.8 192.6-80.6c46.8-43.5 78.1-95.4 93-131.1c3.3-7.9 3.3-16.7 0-24.6c-14.9-35.7-46.2-87.7-93-131.1C433.5 68.8 368.8 32 288 32zM144 256a144 144 0 1 1 288 0 144 144 0 1 1 -288 0zm144-64c0 35.3-28.7 64-64 64c-7.1 0-13.9-1.2-20.3-3.3c-5.5-1.8-11.9 1.6-11.7 7.4c.3 6.9 1.3 13.8 3.2 20.7c13.7 51.2 66.4 81.6 117.6 67.9s81.6-66.4 67.9-117.6c-11.1-41.5-47.8-69.4-88.6-71.1c-5.8-.2-9.2 6.1-7.4 11.7c2.1 6.4 3.3 13.2 3.3 20.3z')
                    .fill('white')
                    .size(bbox.height * 0.6, bbox.height * 0.4)
                    .transform(transform)
                    .move(
                      (bbox.x + bbox.height + bbox.height * 0.2),
                      (bbox.y - bbox.height * 0.75),
                    );
                }
              }

              if (boxes.length > 0) {
                const box = boxes[0];
                if (standActive) {
                  if (onStandClick) {
                    const isClickable = standType === 'stand'
                      && (!onlyFocusClickable || standsInFocusAsText.includes(standNumber));
                    if (isClickable) {
                      box.css({ cursor: 'pointer' });
                      box.click(() => handleStandClick(standNumber));
                    }
                  }
                } else {
                  const { fill: originalColor } = box.attr(['fill']);
                  box.remember('originalColor', originalColor);
                  box.css({ fill: (disableBgColor || '#000000') });
                }
                box.remember('standNumber', standNumber);
                box.remember('standActive', standActive);
                return box;
              }

              return null;
            })
            .filter(Boolean);

          zoomToStands(standsInFocus);
          elementRef.current.style.visibility = 'visible';
          if (elementRef.current.nextSibling) {
            elementRef.current.nextSibling.style.visibility = 'visible';
          }
        });
      }
    }, 20);

    return () => {
      clearTimeout(initTimeoutId);
    };
  }, [
    children,
    stands,
    pois,
    standsInFocus,
    standsFavourites,
    standsVisited,
    bgColor,
    disableBgColor,
    notInFocusOpacity,
    fingersToPan,
    withoutHome,
    withoutZoom,
    withoutPan,
    onlyFocusClickable,
    onStandClick,
    getZoomSettings,
    zoomToStands,
    handleStandClick,
    handleOutsideClick,
    handleBeforePan,
    handleBeforeZoom,
    handleZoom,
  ]);

  useEffect(() => {
    zoomToStands(standsInFocus);
  }, [standsInFocus, zoomToStands]);

  return (
    <>
      <div
        ref={elementRef}
        style={{
          visibility: 'hidden',
          backgroundColor: (bgColor || 'transparent'),
          cursor: (withoutPan ? 'auto' : 'grab'),
        }}
        className="fair-interactive-map"
      >
        {React.isValidElement(children) ? children : null}
      </div>
      {!withoutControls && (
        <div
          className="fair-interactive-map-zoom-controls"
          style={{
            visibility: 'hidden',
            paddingRight: Math.abs((panGutter?.right || 30) - (panGutter?.left || 30)),
            paddingBottom: Math.abs((panGutter?.bottom || 30) - (panGutter?.top || 30)),
          }}
        >
          <button
            ref={entranceButtonElement}
            style={{ display: 'none' }}
            className="fair-interactive-map-zoom-controls__control fair-interactive-map-zoom-controls__control--home"
            type="button"
            onClick={handleHome}
          >
            <span>go to entrance</span>
            <Home />
          </button>
          <button
            ref={zoomInElementRef}
            className="fair-interactive-map-zoom-controls__control fair-interactive-map-zoom-controls__control--zoom-in"
            type="button"
            onClick={handleZoomIn}
          >
            <span>zoom in</span>
            <PlusLight />
          </button>
          <button
            ref={zoomOutElementRef}
            className="fair-interactive-map-zoom-controls__control fair-interactive-map-zoom-controls__control--zoom-out"
            type="button"
            onClick={handleZoomOut}
          >
            <span>zoom out</span>
            <MinusLight />
          </button>
        </div>
      )}
    </>
  );
}

export { FairInteractiveMap };

FairInteractiveMap.propTypes = {
  children: PropTypes.shape({ id: PropTypes.string }),
  stands: PropTypes.arrayOf(identityModel),
  pois: PropTypes.arrayOf(identityModel),
  standsInFocus: PropTypes.arrayOf(identityModel),
  standsFavourites: PropTypes.arrayOf(identityModel),
  standsVisited: PropTypes.arrayOf(identityModel),
  panGutter: PropTypes.shape({
    top: PropTypes.number,
    right: PropTypes.number,
    bottom: PropTypes.number,
    left: PropTypes.number,
  }),
  bgColor: PropTypes.string,
  disableBgColor: PropTypes.string,
  notInFocusOpacity: PropTypes.number,
  fingersToPan: PropTypes.number,
  withoutControls: PropTypes.bool,
  withoutHome: PropTypes.bool,
  withoutZoom: PropTypes.bool,
  withoutPan: PropTypes.bool,
  onlyFocusClickable: PropTypes.bool,
  onStandClick: PropTypes.func,
  onOutsideClick: PropTypes.func,
};

FairInteractiveMap.defaultProps = {
  children: null,
  stands: [],
  pois: [],
  standsInFocus: [],
  standsFavourites: [],
  standsVisited: [],
  panGutter: null,
  bgColor: null,
  disableBgColor: null,
  notInFocusOpacity: null,
  fingersToPan: 1,
  withoutControls: false,
  withoutHome: false,
  withoutZoom: false,
  withoutPan: false,
  onlyFocusClickable: false,
  onStandClick: null,
  onOutsideClick: null,
};
